import { Entity } from '@backstage/catalog-model';

export const TOOL_CATEGORY_ANNOTATION = 'company.io/tool-category';
export const TOOL_REPO_ANNOTATION = 'company.io/tool-repo';
export const TOOL_DOC_ANNOTATION = 'company.io/tool-doc';

export const INTERNAL_TOOLS_FILTER = {
  kind: 'Resource',
  'spec.type': 'internal-tool',
};

export const INTERNAL_TOOL_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.title',
  'metadata.description',
  'metadata.annotations',
  'metadata.tags',
  'metadata.links',
  'spec.owner',
  'spec.type',
];

export type ToolCategoryDefinition = {
  id: string;
  label: string;
};

export type ToolCategoryGroup = ToolCategoryDefinition & {
  tools: Entity[];
};

export type ToolOpenLink = {
  url: string;
  title?: string;
  icon?: string;
};

export type ToolAnnotationLink = {
  url: string;
};

const OTHER_TOOL_CATEGORY: ToolCategoryDefinition = {
  id: 'other',
  label: 'Other',
};

const normalizeCategoryValue = (value: string): string =>
  value
    .trim()
    .toLocaleLowerCase()
    .replace(/[\s_]+/g, '-');

const normalizeString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeForSearch = (value: unknown): string =>
  normalizeString(value)?.toLocaleLowerCase() ?? '';

export const getToolTitle = (entity: Entity): string =>
  normalizeString(entity.metadata.title) ?? entity.metadata.name;

export const getToolDescription = (entity: Entity): string | undefined =>
  normalizeString(entity.metadata.description);

export const getToolTags = (entity: Entity): string[] =>
  (entity.metadata.tags ?? []).map(normalizeString).filter(Boolean) as string[];

export const getToolCategory = (entity: Entity): ToolCategoryDefinition => {
  const rawCategory = normalizeString(
    entity.metadata.annotations?.[TOOL_CATEGORY_ANNOTATION],
  );

  if (!rawCategory) {
    return OTHER_TOOL_CATEGORY;
  }

  return {
    id: normalizeCategoryValue(rawCategory),
    label: rawCategory,
  };
};

export const getToolOpenLink = (entity: Entity): ToolOpenLink | undefined => {
  const link = entity.metadata.links?.find(candidate =>
    Boolean(normalizeString(candidate.url)),
  );

  if (!link) {
    return undefined;
  }

  return {
    url: link.url.trim(),
    title: normalizeString(link.title),
    icon: normalizeString(link.icon),
  };
};

const getToolAnnotationLink = (
  entity: Entity,
  annotation: string,
): ToolAnnotationLink | undefined => {
  const url = normalizeString(entity.metadata.annotations?.[annotation]);

  return url ? { url } : undefined;
};

export const getToolRepoLink = (
  entity: Entity,
): ToolAnnotationLink | undefined =>
  getToolAnnotationLink(entity, TOOL_REPO_ANNOTATION);

export const getToolDocLink = (
  entity: Entity,
): ToolAnnotationLink | undefined =>
  getToolAnnotationLink(entity, TOOL_DOC_ANNOTATION);

export const matchesToolSearch = (entity: Entity, query: string): boolean => {
  const normalizedQuery = normalizeForSearch(query);

  if (!normalizedQuery) {
    return true;
  }

  return [
    getToolTitle(entity),
    entity.metadata.name,
    getToolDescription(entity),
    ...getToolTags(entity),
  ].some(value => normalizeForSearch(value).includes(normalizedQuery));
};

const compareTools = (first: Entity, second: Entity): number => {
  const titleComparison = getToolTitle(first).localeCompare(
    getToolTitle(second),
    undefined,
    { sensitivity: 'base' },
  );

  if (titleComparison !== 0) {
    return titleComparison;
  }

  return first.metadata.name.localeCompare(second.metadata.name, undefined, {
    sensitivity: 'base',
  });
};

const compareCategories = (
  first: ToolCategoryGroup,
  second: ToolCategoryGroup,
): number => {
  if (
    first.id === OTHER_TOOL_CATEGORY.id &&
    second.id !== OTHER_TOOL_CATEGORY.id
  ) {
    return 1;
  }

  if (
    second.id === OTHER_TOOL_CATEGORY.id &&
    first.id !== OTHER_TOOL_CATEGORY.id
  ) {
    return -1;
  }

  return first.label.localeCompare(second.label, undefined, {
    sensitivity: 'base',
  });
};

export const groupToolsByCategory = (
  entities: Entity[],
  query: string,
): ToolCategoryGroup[] => {
  const groups = new Map<string, ToolCategoryGroup>();

  for (const entity of entities) {
    if (!matchesToolSearch(entity, query)) {
      continue;
    }

    const category = getToolCategory(entity);
    const group = groups.get(category.id) ?? { ...category, tools: [] };
    group.tools.push(entity);
    groups.set(category.id, group);
  }

  for (const group of groups.values()) {
    group.tools.sort(compareTools);
  }

  return Array.from(groups.values()).sort(compareCategories);
};
